import { useEffect, useState } from 'react'
import {
  View,
  StyleSheet,
  LayoutChangeEvent,
  ViewStyle,
  TouchableWithoutFeedback,
  GestureResponderEvent,
} from 'react-native'
import { Gesture, GestureDetector } from 'react-native-gesture-handler'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  SharedValue,
  runOnJS,
  withRepeat,
  withTiming,
  withSequence,
  Easing,
} from 'react-native-reanimated'

interface ReplaySliderProps {
  progress: number // 0 to 1
  onSeek: (position: number) => void
  totalSteps: number
  disabled?: boolean
  isLoading?: boolean
}

const Thumb = ({
  width,
  isPressed,
  position,
}: {
  width: number
  isPressed: SharedValue<boolean>
  position: SharedValue<number>
}) => {
  const styles = StyleSheet.create({
    thumb: {
      width: width,
      height: width,
      borderRadius: width / 2,
      backgroundColor: 'black',
      elevation: 3,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
    },
    position: {
      position: 'absolute',
      marginTop: 0,
      left: 0,
      zIndex: 1,
    },
  })

  const thumbAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: position.value }],
    }
  })

  const circleAnimatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ scale: withSpring(isPressed.value ? 1.1 : 1) }],
      backgroundColor: '#666666',
      elevation: isPressed.value ? 4 : 2,
      shadowColor: '#000000',
      shadowOffset: {
        width: 0,
        height: isPressed.value ? 3 : 2,
      },
      shadowOpacity: withSpring(isPressed.value ? 0.25 : 0.15),
      shadowRadius: withSpring(isPressed.value ? 4 : 3),
    }
  })
  return (
    <Animated.View style={[styles.position, thumbAnimatedStyle]}>
      <Animated.View style={[styles.thumb, circleAnimatedStyle]} />
    </Animated.View>
  )
}

export const ReplaySlider = ({
  progress,
  onSeek,
  totalSteps,
  disabled = false,
  isLoading = false,
}: ReplaySliderProps) => {
  const [width, setWidth] = useState(0)
  const isPressed = useSharedValue(false)
  const position = useSharedValue(0)
  const startPosition = useSharedValue(0)

  // Animation value for loading effect
  const loadingAnimation = useSharedValue(1)

  // Handle direct touch on the timeline bar
  const handleBarPress = (event: GestureResponderEvent) => {
    if (disabled) return

    // Calculate the new position based on touch location
    const newPosition = Math.max(
      0,
      Math.min(
        width,
        (event.nativeEvent as unknown as { offsetX: number }).offsetX
      )
    )

    // Animate the thumb to the new position
    position.value = withSpring(newPosition, {
      damping: 20,
      stiffness: 90,
    })

    // Calculate progress and seek
    const newProgress = calculateProgressFromPosition(newPosition)
    runOnJS(onSeek)(newProgress)
  }

  // Calculate the appropriate progress from a position value
  const calculateProgressFromPosition = (positionValue = position.value) => {
    if (width === 0 || totalSteps <= 0) return 0

    // Convert pixel position to progress ratio (0-1)
    const ratio = positionValue / width

    // Convert ratio to step index (rounded to nearest step)
    // Make sure the result is a valid number and within range
    const result = Math.round(ratio * totalSteps)
    return isNaN(result) ? 0 : Math.max(0, Math.min(result, totalSteps))
  }

  const panGesture = Gesture.Pan()
    .enabled(!disabled && !isLoading)
    .onBegin(() => {
      startPosition.value = position.value
      isPressed.value = true
    })
    .onUpdate((e) => {
      position.value = Math.max(
        0,
        Math.min(width, startPosition.value + e.translationX)
      )
    })
    .onEnd(() => {
      isPressed.value = false
      // Use the calculated step index
      const newProgress = calculateProgressFromPosition()
      runOnJS(onSeek)(newProgress)
    })

  const progressStyle = useAnimatedStyle(() => ({
    width: position.value,
    opacity: isLoading ? loadingAnimation.value : 1, // Apply animation when loading
  }))

  const onLayout = (event: LayoutChangeEvent) => {
    const { width: containerWidth } = event.nativeEvent.layout
    setWidth(containerWidth)
  }

  useEffect(() => {
    if (width === 0 || totalSteps <= 0) return

    // Validate progress value to avoid NaN
    const validProgress = isNaN(progress)
      ? 0
      : Math.max(0, Math.min(progress, totalSteps))

    // Calculate the position based on progress and total width
    const progressRatio = validProgress / totalSteps
    const newPosition = width * progressRatio

    // Update the position with animation
    position.value = withSpring(newPosition, {
      damping: 20,
      stiffness: 90,
    })
  }, [progress, width, totalSteps, position])

  // Start or stop loading animation
  useEffect(() => {
    if (isLoading) {
      // Pulse animation for loading state
      loadingAnimation.value = withRepeat(
        withSequence(
          withTiming(0.6, { duration: 700, easing: Easing.inOut(Easing.ease) }),
          withTiming(1, { duration: 700, easing: Easing.inOut(Easing.ease) })
        ),
        -1, // Repeat indefinitely
        true // Reverse
      )
    } else {
      // Reset to full opacity when not loading
      loadingAnimation.value = withTiming(1, { duration: 300 })
    }
  }, [isLoading, loadingAnimation])

  // Create a separate animated style for the loading effect
  const loadingStyle = useAnimatedStyle(() => {
    if (!isLoading) return {}

    return {
      opacity: loadingAnimation.value,
      backgroundColor: '#ff6666', // Lighter red for loading
    }
  })

  return (
    <View style={styles.container} onLayout={onLayout}>
      <TouchableWithoutFeedback onPress={handleBarPress}>
        <View style={styles.barContainer}>
          <View style={styles.timeline} />
          <Animated.View
            style={[styles.progress, progressStyle, loadingStyle]}
          />
        </View>
      </TouchableWithoutFeedback>
      <GestureDetector gesture={panGesture}>
        <Thumb width={12} isPressed={isPressed} position={position} />
      </GestureDetector>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    display: 'flex',
    marginVertical: 14,
    position: 'relative',
    alignItems: 'center',
  },
  barContainer: {
    position: 'absolute',
    width: '100%',
    height: 20, // Make the touch target larger
    justifyContent: 'center',
  },
  timeline: {
    height: 4,
    borderRadius: 2,
    width: '100%',
    backgroundColor: '#757575',
  },
  progress: {
    height: 4,
    borderRadius: 2,
    width: 0,
    backgroundColor: 'red',
    position: 'absolute',
  },
  marker: {
    position: 'absolute',
    width: 2,
    height: 8,

    marginLeft: -1,
  },
})
